import endPoints from 'utils/endPoints';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiRequest from 'helpers/apiRequest';
import { CreateDeliveryNotePayload, FetchDeliveryNotesFilters } from 'services/deliveryNotes';

// Fetch Delivery Notes (POST with filters)
export const useFetchDeliveryNotes = (filters?: FetchDeliveryNotesFilters) => {
  const { data, isPending, isSuccess, refetch, isError } = useQuery({
    queryKey: ['delivery-notes', filters],
    queryFn: async () => {
      const response = await apiRequest({
        url: endPoints.deliveryNotes,
        method: 'POST',
        payload: filters || {},
      });
      return response;
    },
  });

  return {
    loadingDeliveryNotes: isPending,
    deliveryNotesData: data?.data?.data ?? null,
    deliveryNotesResponse: data?.data?.message,
    deliveryNotesSuccess: isSuccess,
    deliveryNotesIsError: isError,
    requestDeliveryNotes: refetch,
  };
};

// Get Delivery Note Details (GET)
export const useDeliveryNoteDetails = (id: number) => {
  const { data, isPending, isSuccess, refetch, isError } = useQuery({
    queryKey: ['delivery-note-details', id],
    queryFn: async () => {
      const response = await apiRequest({
        url: endPoints.deliveryNoteDetails(id),
        method: 'GET',
      });
      return response;
    },
    enabled: !!id,
  });

  return {
    loadingDNDetails: isPending,
    dnDetailsData: data?.data ?? null,
    dnDetailsResponse: data?.data?.message,
    dnDetailsSuccess: isSuccess,
    dnDetailsIsError: isError,
    requestDNDetails: refetch,
  };
};

// Create Delivery Note (POST)
export const useCreateDeliveryNote = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutateAsync, isError, reset } = useMutation({
    mutationFn: async (payload: CreateDeliveryNotePayload) => {
      const response = await apiRequest({
        url: endPoints.createDeliveryNote,
        method: 'POST',
        payload,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delivery-notes'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] }); // PO status might change
    },
  });

  return {
    loadingCreateDN: isPending,
    createDNData: data?.data ?? null,
    createDNResponse: data?.data?.message,
    createDNSuccess: isSuccess,
    createDNIsError: isError,
    createDN: mutateAsync,
    clearCreateDN: reset,
  };
};

// Update Delivery Note QC (POST)
export const useUpdateDeliveryNoteQC = () => {
  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, mutateAsync, isError, reset } = useMutation({
    mutationFn: async ({ id, qcData }: { id: number; qcData: any }) => {
      const response = await apiRequest({
        url: endPoints.updateDeliveryNoteQC(id),
        method: 'POST',
        payload: qcData,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delivery-notes'] });
      queryClient.invalidateQueries({ queryKey: ['delivery-note-details'] });
    },
  });

  return {
    loadingUpdateQC: isPending,
    updateQCData: data?.data ?? null,
    updateQCResponse: data?.data?.message,
    updateQCSuccess: isSuccess,
    updateQCIsError: isError,
    updateQC: mutateAsync,
    clearUpdateQC: reset,
  };
};
